/**
 * Compact map picker for shop locations. Reuses the existing
 * ``react-leaflet`` + OpenStreetMap + Nominatim stack already powering
 * the customer-side ``AddressPickerModal`` so we don't add a new
 * mapping dependency. Designed for inline embedding inside a shop
 * create / edit modal (not a fullscreen modal of its own).
 *
 * Props:
 *   value:      ``{lat, lng} | null`` — current pin position
 *   onChange:   ``({lat, lng, displayName, city, pincode}) => void`` —
 *               fired every time the pin moves (drag, click, search).
 *               The reverse-geocode fields are best-effort; ignore them
 *               if the parent already has its own address inputs.
 *   className:  extra wrapper classes
 *   defaultCenter: starting [lat, lng] when ``value`` is null. Falls
 *                  back to Hyderabad ([17.385, 78.4867]) — matches the
 *                  customer AddressPickerModal so behaviour is
 *                  consistent across the platform.
 *
 * Why inline (not a separate modal)?
 *   Tenant admins editing a shop want to see the map next to the name /
 *   description / pricing fields so they can correlate. The customer
 *   variant uses a modal because it appears in the order checkout
 *   flow where space is at a premium; admin context is wider.
 */
import { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { LocateFixed, MapPin, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { LocationSearchBar } from "./AddressPickerModal";
import PermissionGateModal from "./PermissionGateModal";
import {
    requestLocationPermission, getCurrentLocation,
} from "../lib/geolocation";

// Leaflet's default marker icon path is broken under Webpack / Vite
// because the CSS references images by relative path. Re-pointing to
// the CDN here matches what AddressPickerModal does; we keep both in
// sync. (Idempotent — running twice is a no-op.)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const DEFAULT_CENTER = [17.385, 78.4867];   // Hyderabad

function ClickPicker({ onPick }) {
    useMapEvents({ click(e) { onPick([e.latlng.lat, e.latlng.lng]); } });
    return null;
}

function Recenter({ to }) {
    const map = useMap();
    useEffect(() => {
        if (to) map.setView(to, Math.max(map.getZoom(), 15));
    }, [to, map]);
    return null;
}

export default function ShopMapPicker({
    value,
    onChange,
    className = "",
    defaultCenter = DEFAULT_CENTER,
}) {
    // Maintain ``pos`` as the canonical [lat, lng] used by the map; we
    // mirror it to the parent via ``onChange`` whenever it shifts. This
    // way the parent can be uncontrolled (null initial value) and the
    // map still has a valid centre.
    const [pos, setPos] = useState(() => {
        if (value?.lat != null && value?.lng != null) return [value.lat, value.lng];
        return defaultCenter;
    });
    const [reverse, setReverse] = useState(null);
    const [locBusy, setLocBusy] = useState(false);
    // Play Store / App Store compliance — inline rationale gate (iter49).
    const [locGate, setLocGate] = useState(null);
    // iter126 — track whether the user has actively interacted with
    // the map/search since mount. The parent uses this flag to decide
    // whether to overwrite the address_line1 / pincode / city inputs.
    //
    // Without this, the initial mount reverse-geocode would clobber the
    // shop's stored address when opening it for edit (issue that led
    // to the ``||`` fallback in the parent). With this flag, we get
    // the best of both worlds:
    //   • Mount / edit: userInitiated=false → parent keeps stored data.
    //   • Search / click / drag: userInitiated=true → parent overwrites.
    const userInteractedRef = useRef(false);
    const markUserInteraction = () => { userInteractedRef.current = true; };

    // Reverse-geocode the dropped pin so we can surface a human-readable
    // address blurb under the map (and feed city/pincode back to the
    // parent for autofill — best effort, may be empty).
    useEffect(() => {
        let cancelled = false;
        const u = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos[0]}&lon=${pos[1]}&zoom=18&addressdetails=1`;
        fetch(u, { headers: { Accept: "application/json" } })
            .then((r) => {
                if (!r.ok) throw new Error(`nominatim reverse ${r.status}`);
                return r.json();
            })
            .then((j) => {
                if (cancelled) return;
                setReverse(j);
                const a = j?.address || {};
                onChange?.({
                    lat: pos[0],
                    lng: pos[1],
                    displayName: j?.display_name || "",
                    city: a.city || a.town || a.village || a.county || "",
                    state: a.state || "",
                    pincode: a.postcode || "",
                    userInitiated: userInteractedRef.current,
                });
            })
            .catch(() => {
                // Reverse geocode failure is non-fatal — surface lat/lng only.
                onChange?.({ lat: pos[0], lng: pos[1], userInitiated: userInteractedRef.current });
            });
        return () => { cancelled = true; };
        // ``onChange`` intentionally NOT in the dep array — callers
        // typically inline a new closure each render, which would
        // re-fire the reverse-geocode every keystroke. The pin
        // position is the only meaningful trigger.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pos[0], pos[1]]);

    const useCurrentLocation = async () => {
        // iter102b — same wedge concern that iter54.5 hit on
        // ``AddressPickerModal``: on production Android devices
        // ``Geo.checkPermissions()`` (called by ``checkLocationPermission``)
        // has been observed to block the JS event loop for so long
        // that even our ``_withTimeout(2000)`` race can't fire — the
        // button "does nothing". Skip the upfront probe entirely and
        // call ``getCurrentLocation`` directly; it maps a
        // "denied" / "permission" error from EITHER the Capacitor
        // plugin or the WebView to the same gate-modal flow via
        // ``r.state === "denied"``. Matches the customer address
        // picker's proven pattern.
        await fetchCurrentCoords();
    };

    const fetchCurrentCoords = async () => {
        setLocBusy(true);
        const r = await getCurrentLocation();
        setLocBusy(false);
        if (r.state === "granted" && r.coords) {
            markUserInteraction();
            setPos([r.coords.lat, r.coords.lng]);
            return;
        }
        if (r.state === "denied") { setLocGate("denied"); return; }
        if (r.state === "unsupported") {
            toast.error("Location not supported on this device");
            return;
        }
        // "prompt" or "error" — surface the rationale modal so the user
        // can trigger the OS-level permission dialog explicitly.
        if (r.state === "prompt") { setLocGate("prompt"); return; }
        toast.error(r.error || "Couldn't fetch location");
    };

    return (
        <div className={className} data-testid="shop-map-picker">
            <LocationSearchBar
                onPick={(p) => { markUserInteraction(); setPos(p); }}
                placeholder="Search shop location…"
                testid="shop-loc-search"
            />
            <div className="relative mt-2 bg-muted rounded-2xl overflow-hidden" style={{ height: 280 }}>
                <MapContainer center={pos} zoom={value ? 16 : 12} style={{ height: "100%", width: "100%" }} data-testid="shop-map">
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution="© OpenStreetMap"
                    />
                    <Recenter to={pos} />
                    <ClickPicker onPick={(p) => { markUserInteraction(); setPos(p); }} />
                    <Marker
                        position={pos}
                        draggable
                        eventHandlers={{
                            dragend(e) {
                                markUserInteraction();
                                const ll = e.target.getLatLng();
                                setPos([ll.lat, ll.lng]);
                            },
                        }}
                    />
                </MapContainer>
                <button
                    type="button"
                    data-testid="shop-map-use-current"
                    onClick={useCurrentLocation}
                    disabled={locBusy}
                    className="absolute z-[400] right-3 bottom-3 bg-card border border-border shadow-md rounded-full px-3 py-2 text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60"
                    title="Use current location">
                    {locBusy ? <Loader2 size={14} className="animate-spin" /> : <LocateFixed size={14} className="text-primary" />}
                    Current
                </button>
            </div>
            <div className="mt-2 bg-muted/50 rounded-xl p-2.5 text-xs flex items-start gap-2">
                <MapPin size={14} className="text-primary mt-0.5 shrink-0" />
                <div className="flex-1">
                    <div className="font-mono text-[11px] text-muted-foreground">
                        {pos[0].toFixed(5)}, {pos[1].toFixed(5)}
                    </div>
                    {reverse?.display_name && (
                        <div className="line-clamp-2" data-testid="shop-map-address">
                            {reverse.display_name}
                        </div>
                    )}
                </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">
                Tap anywhere on the map, drag the pin, or use the search bar. The shop is
                auto-assigned to a delivery zone based on this location — or kept under
                <span className="font-semibold"> Unassigned </span> if none match.
            </p>
            {locGate && (
                <PermissionGateModal
                    permission="location"
                    state={locGate}
                    onClose={() => setLocGate(null)}
                    onRequest={async () => {
                        // iter102c — requestLocationPermission returns
                        // a structured ``{state, coords, error}`` object,
                        // NOT a bare string. Comparing against the raw
                        // value (as this callsite used to) meant the
                        // "granted" branch never fired after the user
                        // tapped Continue in the rationale modal — the
                        // modal would stay open and the coords never
                        // applied. Match the contract used by
                        // CustomerHeader + AdminZones.
                        const next = await requestLocationPermission();
                        if (next.state === "granted") {
                            setLocGate(null);
                            if (next.coords) setPos([next.coords.lat, next.coords.lng]);
                            else fetchCurrentCoords();
                        } else if (next.state === "denied") {
                            setLocGate("denied");
                        }
                        return next;
                    }}
                />
            )}
        </div>
    );
}
